import { useState, useEffect, useRef, useCallback } from 'react';

const arrowBtn = (side) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: 8,
  background: 'rgba(0,0,0,0.45)',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: 44,
  height: 44,
  fontSize: 28,
  lineHeight: 1,
  cursor: 'pointer',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  transition: 'background 0.2s',
});

export const MediaCarousel = ({ media, dark }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [containerHeight, setContainerHeight] = useState(null);
  const [sizes, setSizes] = useState({});

  const ZOOM_ANIM_MS = 250;
  const closeZoom = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsZoomed(false);
      setIsClosing(false);
    }, ZOOM_ANIM_MS);
  }, []);
  const openZoom = useCallback(() => {
    setIsClosing(false);
    setIsZoomed(true);
  }, []);
  const viewportRef = useRef(null);
  const hoverRef = useRef(false);
  const touchStartX = useRef(null);
  const swipedRef = useRef(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => {
      setViewportWidth(el.clientWidth);
      setIsMobile(window.innerWidth <= 768);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const total = media?.length ?? 0;
    if (total <= 1) return;
    const onKeyDown = (e) => {
      // Only cycle the carousel the pointer is over (or the open zoom view),
      // so arrow keys don't move every carousel on the page at once.
      if (!hoverRef.current && !isZoomed) return;
      if (e.key === 'Escape' && isZoomed) {
        closeZoom();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentIndex(i => (i - 1 + total) % total);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex(i => (i + 1) % total);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [media, isZoomed, closeZoom]);

  useEffect(() => {
    if (!media || media.length === 0) return;
    const hasVideo = media.some(m => m.type === 'video');
    const imageItems = media.filter(m => m.type === 'image');

    if (imageItems.length === 0) {
      setContainerHeight(hasVideo ? 720 : 420);
      return;
    }

    let maxH = hasVideo ? 720 : 0;
    let pending = imageItems.length;

    imageItems.forEach(({ src }) => {
      const img = new window.Image();
      img.onload = () => {
        maxH = Math.max(maxH, Math.min(img.naturalHeight, 720));
        setSizes(prev => (prev[src] ? prev : { ...prev, [src]: { w: img.naturalWidth, h: img.naturalHeight } }));
        if (--pending === 0) setContainerHeight(maxH);
      };
      img.onerror = () => {
        if (--pending === 0) setContainerHeight(maxH || 420);
      };
      img.src = src;
    });
  }, [media]);

  if (!media || media.length === 0) return null;

  const total = media.length;
  const current = media[currentIndex];

  const goLeft = () => setCurrentIndex(i => (i - 1 + total) % total);
  const goRight = () => setCurrentIndex(i => (i + 1) % total);

  // Swipe navigation (used on touch devices in place of the arrow buttons).
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (total <= 1 || Math.abs(dx) <= 40) return;
    // Mark this as a swipe so the trailing click doesn't also open the zoom view.
    swipedRef.current = true;
    setTimeout(() => { swipedRef.current = false; }, 0);
    if (dx > 0) goLeft();
    else goRight();
  };

  const slotW = viewportWidth > 0 ? viewportWidth * (isMobile ? 0.85 : 0.42) : 0;
  const trackX = viewportWidth / 2 - (currentIndex * slotW + slotW / 2);
  // On mobile, size the viewport to the current (width-constrained) image so
  // there's no empty vertical space around it. Falls back to a fraction of the
  // tallest image while sizes are still loading / for videos.
  const activeScale = isMobile ? 1 : 1.12;
  let height = Math.round((containerHeight ?? 420) * (isMobile ? 0.65 : 1));
  if (isMobile) {
    const size = sizes[current.src];
    const innerW = Math.max(0, slotW - 20); // slot has 10px horizontal padding each side
    const maxH = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.7) : 700;
    if (current.type === 'image' && size && innerW > 0) {
      const dispW = Math.min(size.w, innerW);
      height = Math.min(maxH, Math.round((dispW * size.h) / size.w) + 12); // + border/breathing room
    }
  }
  // Mobile: let the image fill the fitted viewport; desktop: leave headroom for the scaled-up center.
  const maxImgH = isMobile ? height - 8 : Math.round(height * 0.8);

  return (
    <div style={{ marginBottom: 12, userSelect: 'none' }}>
      <div
        ref={viewportRef}
        onMouseEnter={() => { hoverRef.current = true; }}
        onMouseLeave={() => { hoverRef.current = false; }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ position: 'relative', overflow: 'hidden', height, transition: 'height 0.35s cubic-bezier(0.22, 0.61, 0.36, 1)' }}
      >
        <div
          style={{
            display: 'flex',
            height: '100%',
            transform: `translate3d(${trackX}px, 0, 0)`,
            transition: 'transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)',
            willChange: 'transform',
          }}
        >
          {media.map((item, idx) => {
            const active = idx === currentIndex;
            return (
              <div
                key={idx}
                style={{
                  flex: `0 0 ${slotW}px`,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 10px',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  onClick={() => {
                    if (swipedRef.current) return;
                    if (active) openZoom(); else setCurrentIndex(idx);
                  }}
                  style={{
                    position: 'relative',
                    zIndex: active ? 2 : 1,
                    display: 'inline-block',
                    maxWidth: '100%',
                    transform: active ? `scale(${activeScale})` : 'scale(0.72)',
                    opacity: active ? 1 : 0.55,
                    filter: active ? 'none' : 'blur(0.5px)',
                    transition: 'transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.45s, filter 0.45s',
                    cursor: active ? 'zoom-in' : 'pointer',
                  }}
                >
                  {item.type === 'video' ? (
                    <>
                      <video
                        key={item.src}
                        controls={active}
                        style={{ maxWidth: '100%', maxHeight: maxImgH, borderRadius: 8, border: '4px solid rgba(0,0,0,0.1)', display: 'block', pointerEvents: active ? 'auto' : 'none' }}
                      >
                        <source src={item.src} type="video/mp4" />
                      </video>
                      {active && (
                        <button
                          onClick={e => { e.stopPropagation(); openZoom(); }}
                          title="Expand"
                          style={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            background: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 8px',
                            fontSize: 13,
                            cursor: 'pointer',
                            zIndex: 5,
                          }}
                        >⛶ Expand</button>
                      )}
                    </>
                  ) : (
                    <img
                      src={item.src}
                      alt=""
                      draggable={false}
                      style={{
                        maxWidth: '100%',
                        maxHeight: maxImgH,
                        objectFit: 'contain',
                        border: '4px solid rgba(0,0,0,0.1)',
                        borderRadius: 8,
                        background: 'white',
                        display: 'block',
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {total > 1 && (
          <button
            onClick={goLeft}
            style={arrowBtn('left')}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
          >‹</button>
        )}

        {total > 1 && (
          <button
            onClick={goRight}
            style={arrowBtn('right')}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
          >›</button>
        )}
      </div>

      {current.caption && (
        <div style={{
          textAlign: 'center',
          color: dark ? '#fff' : '#000',
          fontSize: 20,
          lineHeight: 1.4,
          marginTop: 10,
          padding: '0 12px',
        }}>
          {current.caption}
        </div>
      )}

      {total > 1 && (
        <div style={{ textAlign: 'center', color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)', fontSize: 13, marginTop: 4 }}>
          {currentIndex + 1} / {total}
        </div>
      )}

      {total > 1 && !isMobile && (
        <div style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          padding: '10px 4px 4px',
          justifyContent: total <= 8 ? 'center' : 'flex-start',
          scrollbarWidth: 'thin',
        }}>
          {media.map((item, idx) => (
            <div
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              style={{
                cursor: 'pointer',
                border: `3px solid ${idx === currentIndex ? '#0d6efd' : 'transparent'}`,
                borderRadius: 6,
                overflow: 'hidden',
                flexShrink: 0,
                opacity: idx === currentIndex ? 1 : 0.55,
                transition: 'opacity 0.2s, border-color 0.2s',
              }}
            >
              {item.type === 'video' ? (
                <div style={{
                  width: 64,
                  height: 64,
                  background: '#1a1a1a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 22,
                }}>▶</div>
              ) : (
                <img src={item.src} alt="" style={{ width: 64, height: 64, objectFit: 'cover', display: 'block' }} />
              )}
            </div>
          ))}
        </div>
      )}

      {isZoomed && (
        <div
          onClick={e => e.target === e.currentTarget && closeZoom()}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            animation: `${isClosing ? 'fadeOut' : 'fadeIn'} ${ZOOM_ANIM_MS}ms ease-in-out forwards`,
          }}
        >
          <button
            onClick={() => closeZoom()}
            style={{
              position: 'fixed',
              top: 20,
              right: 24,
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              fontSize: 22,
              cursor: 'pointer',
              zIndex: 51,
            }}
          >✕</button>

          {current.caption && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '0 16px 22px',
                display: 'flex',
                justifyContent: 'center',
                zIndex: 51,
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  maxWidth: 'min(90vw, 900px)',
                  textAlign: 'center',
                  color: '#fff',
                  fontSize: 21,
                  lineHeight: 1.45,
                  background: 'rgba(0,0,0,0.8)',
                  padding: '12px 20px',
                  borderRadius: 12,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
                }}
              >
                {current.caption}
              </div>
            </div>
          )}

          {total > 1 && (
            <button
              onClick={e => { e.stopPropagation(); goLeft(); }}
              style={{ ...arrowBtn('left'), left: 24 }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
            >‹</button>
          )}

          {current.type === 'video' ? (
            <video
              key={current.src}
              controls
              autoPlay
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                borderRadius: 12,
                border: '8px solid rgba(255,255,255,0.85)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                animation: `${isClosing ? 'zoomOut' : 'zoomIn'} ${ZOOM_ANIM_MS}ms ease-in-out forwards`,
              }}
            >
              <source src={current.src} type="video/mp4" />
            </video>
          ) : (
            <img
              src={current.src}
              alt=""
              onClick={() => window.open(current.src, '_blank')}
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                border: '8px solid rgba(255,255,255,0.85)',
                borderRadius: 12,
                background: 'white',
                boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                animation: `${isClosing ? 'zoomOut' : 'zoomIn'} ${ZOOM_ANIM_MS}ms ease-in-out forwards`,
                cursor: 'pointer',
              }}
            />
          )}

          {total > 1 && (
            <button
              onClick={e => { e.stopPropagation(); goRight(); }}
              style={{ ...arrowBtn('right'), right: 24 }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
            >›</button>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fadeOut { from { opacity: 1 } to { opacity: 0 } }
        @keyframes zoomIn { from { transform: scale(0.85) } to { transform: scale(1) } }
        @keyframes zoomOut { from { transform: scale(1) } to { transform: scale(0.85) } }
      `}</style>
    </div>
  );
};
